// Customer types: where one product stands on one type's menu, and what the product
// form's "Shown to <type>" tick writes. Shared by the resolver (server/services/
// menuPricing.ts), the rule routes (server/customerTypeRoutes.ts) and the admin screens
// (client/src/lib/customerTypes.ts). Pure functions only.
//
// Two facts decide it, strongest first:
//   - the type hides the product's whole category → hidden, and nothing on the product
//     can undo it;
//   - the type has a hide rule on the product itself → hidden.
// Anything else is shown. A type never hides by sell unit (Jon, 2026-10-14): a new
// product, whatever its unit, shows to every type until someone unticks it — as an
// account's own menu switches work. customer_types.hidden_units is no longer read, and a
// product rule with hidden = false (the retired "show anyway") means nothing.

export type TypeMenuState = "shown" | "hidden_by_category" | "hidden_by_product";

/** A type's rule on one product: hide it, or no rule. */
export type ProductMenuRule = "hide" | null;

export interface TypeMenuFacts {
  categoryHidden: boolean;
  rule: ProductMenuRule;
}

export function menuStateFrom(facts: TypeMenuFacts): TypeMenuState {
  if (facts.categoryHidden) return "hidden_by_category";
  if (facts.rule === "hide") return "hidden_by_product";
  return "shown";
}

export function isShownState(state: TypeMenuState): boolean {
  return state === "shown";
}

/**
 * customer_type_menu_rules.hidden on a product row ↔ the rule it stands for. A row with
 * hidden = false (an old "show anyway") is no rule at all.
 */
export function productRuleOf(row: { hidden: boolean } | null | undefined): ProductMenuRule {
  return row?.hidden ? "hide" : null;
}

/**
 * The product rule that leaves a product shown to a type (or not), changing as little
 * as possible: already so → the rule it has. Unticking stores a hide rule; ticking drops
 * it. A category rule cannot be undone from the product.
 */
export function ruleForTick(facts: TypeMenuFacts, shown: boolean): { rule: ProductMenuRule } | { refused: "hidden_by_category" } {
  if (isShownState(menuStateFrom(facts)) === shown) return { rule: facts.rule };
  if (!shown) return { rule: "hide" };
  if (facts.categoryHidden) return { refused: "hidden_by_category" };
  return { rule: null };
}
